import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { getImageUrl } from "../../services/api";

export default function ReviewForm() {
  const { slug } = useParams();
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    rating: null,
    message: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    phone: "",
  });
  const [showThankYou, setShowThankYou] = useState(false);
  const ratingOptions = [
    { value: 5, label: "Excellent" },
    { value: 4, label: "Good" },
    { value: 3, label: "Average" },
    { value: 2, label: "Below Average" },
    { value: 1, label: "Poor" },
  ];

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const isFormValid = () => {
    return (
      formData.email.trim() !== "" &&
      validateEmail(formData.email) &&
      formData.phone.trim() !== "" &&
      validatePhone(formData.phone) &&
      formData.rating !== null
    );
  };

  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ["public-product", slug],
    queryFn: () => api.get(`/public/products/${slug}`).then(res => res.data),
  });

  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        // Try to close the window (only works if opened by script)
        try {
          window.close();
        } catch (e) {
          // Ignore errors
        }
        
        // Always redirect to blank page as fallback (works even if window.close() fails)
        // Use a small delay to ensure window.close() attempt completes first
        setTimeout(() => {
          window.location.replace("about:blank");
        }, 100);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showThankYou]);

  const reviewMutation = useMutation({
    mutationFn: (data) => api.post(`/public/products/${slug}/review/step1`, data),
    onSuccess: async (res) => {
      if (res.data.redirect && res.data.url) {
        // Copy message to clipboard if it exists
        if (formData.message && formData.message.trim()) {
          try {
            await navigator.clipboard.writeText(formData.message.trim());
          } catch (err) {
            // Fallback for older browsers
            try {
              const textArea = document.createElement("textarea");
              textArea.value = formData.message.trim();
              textArea.style.position = "fixed";
              textArea.style.left = "-999999px";
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand("copy");
              document.body.removeChild(textArea);
            } catch (fallbackErr) {
              // If clipboard copy fails, continue with redirect anyway
              console.error("Failed to copy to clipboard:", fallbackErr);
            }
          }
        }
        // Redirect to Amazon
        window.location.href = res.data.url;
      }
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      const dataSaved = errorData?.data_saved;
      const canProceed = errorData?.can_proceed;
      
      if (dataSaved === true && canProceed === false) {
        setShowThankYou(true);
        return;
      }
      
      const errorMessage = errorData?.message || "Something went wrong. Please try again.";
      alert(errorMessage);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    
    const email = form.email.value.trim();
    const phone = form.phone.value.replace(/\D/g, "");
    const rating = parseInt(form.rating.value);

    const errors = {};
    let hasErrors = false;

    if (!rating) {
      errors.rating = "Please select a rating";
      hasErrors = true;
    }

    if (!email) {
      errors.email = "Email is required";
      hasErrors = true;
    } else if (!validateEmail(email)) {
      errors.email = "Please enter a valid email address";
      hasErrors = true;
    }

    if (!phone) {
      errors.phone = "Phone number is required";
      hasErrors = true;
    } else if (!validatePhone(phone)) {
      errors.phone = "Please enter a valid 10-digit Indian phone number";
      hasErrors = true;
    }

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    const data = {
      email,
      phone,
      rating,
    };
    
    // Include message only if provided (it's optional)
    if (formData.message && formData.message.trim()) {
      data.message = formData.message.trim();
    }
    
    setFormData((prev) => ({ ...prev, ...data }));
    reviewMutation.mutate(data);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (field === "email" && value.trim() !== "") {
      if (!validateEmail(value)) {
        setValidationErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
      }
    } else if (field === "phone" && value.trim() !== "") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 10) {
        const limitedValue = digitsOnly.slice(0, 10);
        setFormData((prev) => ({ ...prev, phone: limitedValue }));
        if (!validatePhone(limitedValue)) {
          setValidationErrors((prev) => ({ ...prev, phone: "Please enter a valid 10-digit Indian phone number" }));
        } else {
          setValidationErrors((prev) => ({ ...prev, phone: "" }));
        }
      } else {
        if (digitsOnly.length > 0 && !validatePhone(digitsOnly)) {
          setValidationErrors((prev) => ({ ...prev, phone: "Please enter a valid 10-digit Indian phone number" }));
        } else {
          setValidationErrors((prev) => ({ ...prev, phone: "" }));
        }
        setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      }
    }
  };

  const handleRatingClick = (rating) => {
    setFormData((prev) => ({ ...prev, rating }));
    const form = document.getElementById("review-form");
    if (form) {
      form.rating.value = rating;
    }
    if (validationErrors.rating) {
      setValidationErrors((prev) => ({ ...prev, rating: "" }));
    }
  };

  if (showThankYou) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-primary via-secondary to-primary flex items-center justify-center z-50">
        <div className="text-center px-6">
          <div className="mb-6">
            <svg
              className="w-24 h-24 mx-auto text-white animate-bounce"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Thank You!
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-2">
            Thank you for submitting your review.
          </p>
          <p className="text-lg md:text-xl text-white/80">
            We appreciate your feedback!
          </p>
        </div>
      </div>
    );
  }

  if (productLoading) {
    return (
      <div className="h-screen bg-white flex flex-col">
        <div className="bg-gray-900 h-12 w-full flex-shrink-0"></div>
        <div className="max-w-3xl mx-auto px-4 pt-4 flex-1">
          <div className="text-center py-4 text-gray-600">
            Loading product<span className="animate-pulse">...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="h-screen bg-white flex flex-col">
        <div className="bg-gray-900 h-12 w-full flex-shrink-0"></div>
        <div className="max-w-3xl mx-auto px-4 pt-4 flex-1">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Product not found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Amazon-style dummy header */}
      <div className="bg-gray-900 h-12 w-full flex-shrink-0"></div>
      
      <div className="max-w-3xl mx-auto px-4 py-3 flex-1 overflow-y-auto">
        {/* Amazon-style heading */}
        <h1 className="text-xl font-normal text-gray-900 mb-3" style={{ fontFamily: 'Arial, sans-serif' }}>
          How was the item?
        </h1>

        {/* Product info - Amazon style */}
        <div className="flex items-start gap-3 mb-4 pb-3 border-b border-gray-200">
          <img
            src={getImageUrl(product.image)}
            alt={product.name}
            className="w-16 h-16 object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm text-gray-900 leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
              {product.name}
            </h2>
          </div>
        </div>

        <form id="review-form" onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          {/* Rating Section - Amazon style list */}
          <div className="mb-4">
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: 'Arial, sans-serif' }}>
                Overall rating
              </label>
              <div className="space-y-1">
                {ratingOptions.map((opt) => {
                  const isSelected = formData.rating === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleRatingClick(opt.value)}
                      aria-pressed={isSelected}
                      className={`w-full flex items-center gap-3 px-2 py-1 rounded transition-colors text-left ${
                        isSelected ? "bg-yellow-50" : "hover:bg-gray-50"
                      }`}
                      title={opt.label}
                    >
                      <div className="flex items-center gap-0.5" aria-label={`${opt.value} star${opt.value > 1 ? 's' : ''}`}>
                        {/* Render multiple stars per option; fill them only when this option is selected. */}
                        {Array.from({ length: opt.value }).map((_, i) => (
                          <svg
                            key={i}
                            className="w-6 h-6"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            fill={isSelected ? "#FFA41C" : "none"}
                            stroke={isSelected ? "#FFA41C" : "#D1D5DB"}
                            aria-hidden="true"
                          >
                            <path
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                              strokeWidth={isSelected ? 0 : 1}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ))}
                      </div>
                      <span
                        className={`text-sm ${isSelected ? "text-gray-900 font-semibold" : "text-gray-700"}`}
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {validationErrors.rating && (
                <p className="text-xs text-red-600 mt-1">{validationErrors.rating}</p>
              )}
            </div>
            <input
              name="rating"
              type="hidden"
              value={formData.rating || ""}
              required
            />
          </div>

          {/* Email Field - Amazon style */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: 'Arial, sans-serif' }}>
              Email Address <span className="text-red-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              required
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              style={{ 
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px'
              }}
              className={`w-full px-3 py-1.5 border rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 ${
                validationErrors.email
                  ? "border-red-500 focus:ring-red-400 focus:border-red-500"
                  : "border-gray-400"
              }`}
            />
            {validationErrors.email && (
              <p className="text-xs text-red-600 mt-0.5" style={{ fontFamily: 'Arial, sans-serif' }}>
                {validationErrors.email}
              </p>
            )}
          </div>

          {/* Phone Field - Amazon style */}
          <div className="mb-4">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: 'Arial, sans-serif' }}>
              Phone Number <span className="text-red-600">*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="Enter 10-digit phone number"
              required
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              maxLength={10}
              style={{ 
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px'
              }}
              className={`w-full px-3 py-1.5 border rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 ${
                validationErrors.phone
                  ? "border-red-500 focus:ring-red-400 focus:border-red-500"
                  : "border-gray-400"
              }`}
            />
            {validationErrors.phone && (
              <p className="text-xs text-red-600 mt-0.5" style={{ fontFamily: 'Arial, sans-serif' }}>
                {validationErrors.phone}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Arial, sans-serif' }}>
              Enter 10-digit Indian phone number (starting with 6-9)
            </p>
          </div>

          {/* Message Field - Amazon style (optional) */}
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: 'Arial, sans-serif' }}>
              Add a written feedback (optional)
            </label>
            <textarea
              id="message"
              name="message"
              rows={3}
              placeholder="Share your thoughts about this product..."
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              style={{ 
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px'
              }}
              className="w-full px-3 py-1.5 border border-gray-400 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 resize-y"
            />
            <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Arial, sans-serif' }}>
              Optional: Share your experience with this product
            </p>
          </div>

          {/* Submit Button - Amazon yellow style */}
          <div className="mt-4 pt-3 border-t border-gray-200 pb-4">
            <button
              type="submit"
              style={{ 
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#FFD814',
                borderColor: '#FCD200'
              }}
              className="px-8 py-2 rounded border font-medium shadow-sm transition-colors hover:bg-yellow-500 hover:border-yellow-600 text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={reviewMutation.isPending || !isFormValid()}
            >
              {reviewMutation.isPending ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
